import {
  mergeAttributes,
  Node,
  PasteRule,
} from '@tiptap/core'

export interface FilesOptions {
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    files: {
      setFiles: (options: { fileIds: string[] }) => ReturnType
    }
  }
}

export const Files = Node.create<FilesOptions>({
  name: 'files',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  inline: false,

  group: 'block',

  content: 'file* caption',

  draggable: true,

  addAttributes() {
    return { }
  },

  parseHTML() {
    return [
      {
        tag: 'div[files]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    // TODO caption
    return [
      'div',
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        { class: 'files', files: null },
      ),
      0,
    ]
  },

  renderText({ node }) {
    // format ![caption](url1,url2,...)
    const fileIds = []
    let caption = ''
    for (const child of node.children) {
      switch (child.type.name) {
        case 'file':
          fileIds.push(child.attrs['fileId'])
          break
        case 'caption':
          caption = child.textContent
      }
    }
    return `![${caption}](${fileIds.join(',')})`
  },

  addPasteRules() {
    return [
      new PasteRule({
        // match ![caption](src1,src2,..)
        find: /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:\s+)?\))/g,
        handler({
          match, chain, range,
        }) {
          const [,, caption, srcs] = match
          const fileIds = srcs.split(',')

          if (!fileIds) {
            return null
          }

          const node = {
            type: 'files',
            attrs: {},
            content: [
              ...fileIds.map(fileId => ({ type: 'file', attrs: { fileId } })),
              {
                type: 'caption',
                attrs: {},
                content: [{
                  type: 'text',
                  text: caption,
                }],
              },
            ],
          }

          if (match.input) {
            chain().deleteRange(range).insertContentAt(range.from, node)
          }
        },
      }),
    ]
  },

  addCommands() {
    return {
      setFiles: options => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {},
          content: [
            ...options.fileIds.map(fileId => ({ type: 'file', attrs: { fileId } })),
            {
              type: 'caption',
              attrs: {},
            },
          ],
        })
      },
    }
  },

  addKeyboardShortcuts() {
    return {
      // TODO add to caption to delete files if empty
      // remove code block when at start of document or code block is empty
      Backspace: () => {
        const { empty, $anchor } = this.editor.state.selection
        const isAtStart = $anchor.pos === 1

        if (!empty || $anchor.parent.type.name !== this.name) {
          return false
        }

        if (isAtStart || !$anchor.parent.textContent.length) {
          return this.editor.commands.clearNodes()
        }

        return false
      },
    }
  },
})
